
import { registerLocaleData } from "@angular/common";
import { Injectable, LOCALE_ID, inject, provideAppInitializer } from "@angular/core";
import { loadTranslations } from "@angular/localize";

// Import default locales as fallback
import localeIt from "@angular/common/locales/it";
import localeEn from "@angular/common/locales/en";

// Register default locales immediately to ensure DatePipe works
registerLocaleData(localeIt, "it");
registerLocaleData(localeEn, "en");

@Injectable({
    providedIn: "root"
})
export class I18n {
    /** Lingue attive */
    activeLangs = ["it", "en"];

    /*+ Lingua di default */
    locale = "it";

    constructor() {
        void this.setLocale();
    }

    async setLocale() {
        const userLocale = localStorage.getItem("locale");

        /** Se l'utente ha già una lingua selezionata, controlla se è tra le lingue attive, e quindi la usa. */
        if (userLocale && this.activeLangs.includes(userLocale)) {
            this.locale = userLocale;
        } else {
            /** Se non ha già una lingua settata, controlla se la lingua del browser è tra quelle attive, e quindi la usa. Altrimenti resta quella di default impostata sopra. */
            const browserLang = navigator.language.split("-")[0];
            if (browserLang && this.activeLangs.includes(browserLang)) {
                this.locale = browserLang;
            }
        }
        await this.loadLocaleData();
    }

    /**
     * Load locale data and translations
     */
    private async loadLocaleData() {
        try {
            // Load translation file
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const localeTranslationsModule = await import(
                `src/assets/i18n/${this.locale}.json`
            );
            
            // Load translations for the current locale at run-time
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            loadTranslations(localeTranslationsModule.default);
            
        } catch (error) {
            // Fallback to default locale if loading fails
            if (this.locale !== "it") {
                this.locale = "it";
                await this.loadLocaleData();
            }
        }
    }

    /**
     * Dynamically change locale at runtime
     * @param newLocale - The new locale to set
     */
    async changeLocale(newLocale: string): Promise<void> {
        if (this.activeLangs.includes(newLocale) && this.locale !== newLocale) {
            this.locale = newLocale;
            localStorage.setItem("locale", newLocale);
            await this.loadLocaleData();
        }
    }

    async getLocale(): Promise<{ default: Record<string, string> }> {
        return import(
            `src/assets/i18n/${this.locale}.json`
        ) as Promise<{ default: Record<string, string> }>;
    }
}


// Load locale data at app start-up
function setLocale() {
    return provideAppInitializer(() => {
        const initializerFn = ((i18n: I18n) => () => i18n.setLocale())(inject(I18n));
        return initializerFn();
      });
}

// Set the runtime locale for the app
function setLocaleId() {
    return {
        provide: LOCALE_ID,
        useFactory: (i18n: I18n) => i18n.locale,
        deps: [I18n]
    };
}


/** Per aggiungere una lingua:
 * - Creare il file json della lingua in assets/i18n
 * - In i18n.modules.ts nella funzione setLocale aggiornare la direttiva webpackInclude: /(it|en)\.mjs$/ aggiungendo la lingua
 * - Aggiungerla all'array activeLangs
*/
export const i18nModule = {
    setLocale: setLocale,
    setLocaleId: setLocaleId
};